import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { tasksApi, type CreateTaskInput, type ProjectRef, type TaskBusiness, type TaskCounts, type TaskListResult, type TaskMember, type TaskPriority, type TaskRow, type TaskSort, type TaskStatus } from '../api/tasks';
import { Table, type Column, type TableSort } from '../components/Table';
import { t } from '../i18n';
import { Link, navigate } from '../router';
import { fmtDue, fmtWhen, priorityClass, priorityLabel, statusLabel } from './tasks.format';
import './tasks.css';

const PAGE_SIZE = 25;
/** Chips shown always (mockup order); planned/rejected appear only when they have tasks. */
const CHIP_STATUSES: TaskStatus[] = ['backlog', 'in_progress', 'review', 'waiting_human', 'done', 'deferred'];
const OPTIONAL_CHIPS: TaskStatus[] = ['planned', 'rejected'];
type Chip = 'active' | TaskStatus;
/** Column keys of sortable columns are the server sort names, so the header maps 1:1 to `sort`/`dir`. */
const SERVER_SORTS: TaskSort[] = ['number', 'title', 'priority', 'dueAt', 'updatedAt'];

/** Chips are capitalised like in the mockup («В роботі»); table badges keep the lowercase labels. */
function chipLabel(c: Chip): string {
  const s = c === 'active' ? t('tasks.chip.active') : statusLabel(c);
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function Tasks({ canWrite }: { canWrite: boolean }) {
  const [businesses, setBusinesses] = useState<TaskBusiness[] | null>(null);
  const [members, setMembers] = useState<TaskMember[]>([]);
  const [businessId, setBusinessId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [chip, setChip] = useState<Chip>('active');
  const [sort, setSort] = useState<TableSort | null>(null);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<TaskListResult | null>(null);
  const [counts, setCounts] = useState<TaskCounts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);
  // Every list request gets a sequence number; a response that is not the latest one is dropped.
  const seq = useRef(0);

  useEffect(() => {
    tasksApi.businesses().then(setBusinesses).catch((e: Error) => setError(e.message));
  }, []);

  // Assignee filter follows the business: only people who can be assigned there.
  useEffect(() => {
    tasksApi.members(businessId || undefined).then(setMembers).catch((e: Error) => setError(e.message));
  }, [businessId]);

  // Debounce the search box so typing does not hit the API on every keystroke.
  useEffect(() => {
    const h = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [businessId, assigneeId, q, chip, sort]);

  const load = () => {
    setError(null);
    const mine = ++seq.current;
    return Promise.all([
      tasksApi.list({
        businessId: businessId || undefined,
        assigneeId: assigneeId || undefined,
        q: q || undefined,
        status: chip === 'active' ? undefined : [chip],
        sort: sort ? (sort.key as TaskSort) : undefined,
        dir: sort?.dir,
        page,
        pageSize: PAGE_SIZE,
      }),
      tasksApi.counts(businessId || undefined),
    ])
      .then(([r, c]) => {
        if (mine !== seq.current) return;
        setResult(r);
        setCounts(c);
      })
      .catch((e: Error) => {
        if (mine === seq.current) setError(e.message);
      });
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, assigneeId, q, chip, sort, page]);

  const chips = useMemo<Chip[]>(() => ['active', ...CHIP_STATUSES, ...OPTIONAL_CHIPS.filter((s) => (counts?.[s] ?? 0) > 0 || chip === s)], [counts, chip]);
  const activeBusinesses = useMemo(() => (businesses ?? []).filter((b) => b.status === 'active'), [businesses]);

  const columns: Column<TaskRow>[] = [
    { key: 'number', title: t('tasks.col.number'), render: (r) => <Link to={`/tasks/${r.id}`} className="muted">{r.key}</Link>, sortable: true },
    { key: 'title', title: t('tasks.col.title'), render: (r) => <Link to={`/tasks/${r.id}`}>{r.title}</Link>, sortable: true, wrap: true },
    { key: 'business', title: t('tasks.col.business'), render: (r) => r.business.name },
    { key: 'project', title: t('tasks.col.project'), render: (r) => r.project?.name ?? '—' },
    { key: 'status', title: t('tasks.col.status'), render: (r) => <span className="badge">{statusLabel(r.status)}</span> },
    { key: 'assignee', title: t('tasks.col.assignee'), render: (r) => r.assignee?.displayName ?? '—' },
    { key: 'priority', title: t('tasks.col.priority'), render: (r) => <span className={priorityClass(r.priority)}>{priorityLabel(r.priority)}</span>, sortable: true },
    { key: 'dueAt', title: t('tasks.col.due'), render: (r) => fmtDue(r.dueAt), sortable: true },
    { key: 'updatedAt', title: t('tasks.col.updated'), render: (r) => <span className="muted">{fmtWhen(r.updatedAt)}</span>, sortable: true },
  ];

  const total = result?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>{t('tasks.title')}</h1>
        {canWrite && (
          <button type="button" className="btn" onClick={() => setDrawer(true)} disabled={activeBusinesses.length === 0}>
            {t('tasks.new')}
          </button>
        )}
      </div>
      {error && <div className="error" style={{ marginTop: 'var(--sp-3)' }}>{error}</div>}
      {businesses !== null && businesses.length === 0 && <p className="muted" style={{ marginTop: 'var(--sp-3)' }}>{t('tasks.noBusinesses')}</p>}

      <div className="row filters" style={{ margin: 'var(--sp-4) 0 var(--sp-2)' }}>
        <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} aria-label={t('tasks.col.business')}>
          <option value="">{t('tasks.filter.allBusinesses')}</option>
          {(businesses ?? []).map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} aria-label={t('tasks.col.assignee')}>
          <option value="">{t('tasks.filter.anyAssignee')}</option>
          {members.map((m) => (
            <option key={m.actorId} value={m.actorId}>{m.displayName}</option>
          ))}
        </select>
        <input className="input" placeholder={t('tasks.filter.search')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('tasks.filter.search')} />
      </div>

      <div className="row" style={{ marginBottom: 'var(--sp-3)' }}>
        {chips.map((c) => (
          <button key={c} type="button" className={`chip${chip === c ? ' on' : ''}`} onClick={() => setChip(c)} aria-pressed={chip === c}>
            {chipLabel(c)} {counts ? counts[c] : ''}
          </button>
        ))}
      </div>

      <Table columns={columns} rows={result?.rows ?? []} rowKey={(r) => r.id} empty={t('tasks.empty')} sort={sort} onSort={(s) => setSort(s && SERVER_SORTS.includes(s.key as TaskSort) ? s : null)} />

      <div className="row" style={{ marginTop: 'var(--sp-3)', justifyContent: 'space-between' }}>
        <span className="muted">{t('tasks.shown', { shown: result?.rows.length ?? 0, total, page })}</span>
        {pages > 1 && (
          <span className="row">
            <button type="button" className="btn secondary small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('tasks.prev')}</button>
            <button type="button" className="btn secondary small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>{t('tasks.next')}</button>
          </span>
        )}
      </div>

      {drawer && (
        <NewTaskDrawer
          businesses={activeBusinesses}
          defaultBusinessId={(activeBusinesses.some((b) => b.id === businessId) ? businessId : activeBusinesses[0]?.id) ?? ''}
          onClose={() => setDrawer(false)}
          onCreated={(task) => {
            setDrawer(false);
            navigate(`/tasks/${task.id}`);
          }}
        />
      )}
    </>
  );
}

function NewTaskDrawer({ businesses, defaultBusinessId, onClose, onCreated }: { businesses: TaskBusiness[]; defaultBusinessId: string; onClose: () => void; onCreated: (task: TaskRow) => void }) {
  const [businessId, setBusinessId] = useState(defaultBusinessId);
  const [projects, setProjects] = useState<ProjectRef[]>([]);
  const [members, setMembers] = useState<TaskMember[]>([]);
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [dueAt, setDueAt] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Projects and assignee candidates belong to the chosen business.
  useEffect(() => {
    setProjectId('');
    setAssigneeId('');
    if (!businessId) return;
    setError(null);
    tasksApi.projects(businessId).then(setProjects).catch((e: Error) => setError(e.message));
    tasksApi.members(businessId).then(setMembers).catch((e: Error) => setError(e.message));
  }, [businessId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const input: CreateTaskInput = { businessId, title: title.trim(), projectId: projectId || null, description: description.trim() || null, priority, dueAt: dueAt || null, assigneeId: assigneeId || null };
    try {
      onCreated(await tasksApi.create(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <form className="drawer form" onSubmit={submit} role="dialog" aria-modal="true" aria-label={t('tasks.new')}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>{t('tasks.new')}</strong>
          <button type="button" className="btn secondary small" onClick={onClose}>{t('common.close')}</button>
        </div>
        <label>
          {t('tasks.form.business')}
          <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} required>
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </label>
        <label>
          {t('tasks.form.title')}
          <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={300} autoFocus />
        </label>
        <label>
          {t('tasks.form.project')}
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">{t('tasks.form.none')}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>
        <label>
          {t('tasks.form.description')}
          <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <div className="row">
          <label>
            {t('tasks.form.priority')}
            <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
              {(['normal', 'high', 'urgent', 'low'] as TaskPriority[]).map((p) => (
                <option key={p} value={p}>{priorityLabel(p)}</option>
              ))}
            </select>
          </label>
          <label>
            {t('tasks.form.due')}
            <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
          </label>
        </div>
        <label>
          {t('tasks.form.assignee')}
          <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
            <option value="">{t('tasks.form.none')}</option>
            {members.map((m) => (
              <option key={m.actorId} value={m.actorId}>{m.displayName}</option>
            ))}
            <option disabled>{t('tasks.form.agentsLater')}</option>
          </select>
        </label>
        {error && <div className="error">{error}</div>}
        <div className="row">
          <button className="btn" type="submit" disabled={busy || !businessId}>{t('tasks.form.create')}</button>
          <button className="btn secondary" type="button" onClick={onClose}>{t('common.cancel')}</button>
        </div>
        <div className="note">{t('tasks.form.mobileHint')}</div>
      </form>
    </>
  );
}
